import { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Shield, Eye, Users, Mail, TrendingUp, RefreshCw } from 'lucide-react';

const RANGES = [
  { label: '7d', value: '7d' },
  { label: '30d', value: '30d' },
  { label: '90d', value: '90d' },
];

function StatCard({ icon: Icon, label, value, color = 'violet' }) {
  const colors = {
    violet: 'from-violet-500/20 to-purple-500/20 border-violet-400/30',
    blue: 'from-blue-500/20 to-cyan-500/20 border-blue-400/30',
    green: 'from-green-500/20 to-emerald-500/20 border-green-400/30',
    amber: 'from-amber-500/20 to-orange-500/20 border-amber-400/30',
  };
  return (
    <div className={`bg-gradient-to-br ${colors[color]} border rounded-xl p-4 sm:p-5`}>
      <div className="flex items-center gap-3">
        <Icon className="w-5 h-5 text-gray-400 flex-shrink-0" />
        <div className="min-w-0">
          <p className="text-xs text-gray-400 uppercase tracking-wider">{label}</p>
          <p className="text-xl sm:text-2xl font-bold text-white">{value ?? '—'}</p>
        </div>
      </div>
    </div>
  );
}

function DataTable({ title, headers, rows }) {
  if (!rows?.length) return (
    <div className="text-sm text-gray-500 py-4 text-center">{title}: No data yet</div>
  );
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-white/10">
            {headers.map(h => (
              <th key={h} className="text-left py-2 px-3 text-gray-400 font-medium text-xs uppercase tracking-wider">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-white/5 hover:bg-white/5">
              {row.map((cell, j) => (
                <td key={j} className="py-2 px-3 text-gray-300">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminDashboard() {
  const [token, setToken] = useState(() => sessionStorage.getItem('apdn_admin_token') || '');
  const [authenticated, setAuthenticated] = useState(false);
  const [data, setData] = useState(null);
  const [range, setRange] = useState('30d');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchData = async (t, r) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`/.netlify/functions/admin-analytics?range=${r}`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      if (res.status === 401) {
        setAuthenticated(false);
        sessionStorage.removeItem('apdn_admin_token');
        setError('Invalid token');
        return;
      }
      if (!res.ok) throw new Error('API error');
      const json = await res.json();
      setData(json);
      setAuthenticated(true);
      sessionStorage.setItem('apdn_admin_token', t);
    } catch {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (!token.trim()) return;
    fetchData(token.trim(), range);
  };

  useEffect(() => {
    if (authenticated && token) fetchData(token, range);
  }, [range]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-login if token in session
  useEffect(() => {
    const saved = sessionStorage.getItem('apdn_admin_token');
    if (saved) {
      setToken(saved);
      fetchData(saved, range);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!authenticated) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <form onSubmit={handleLogin} className="w-full max-w-sm bg-white/5 border border-white/10 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <Shield className="w-6 h-6 text-violet-400" />
            <h1 className="text-xl font-bold text-white">Admin Dashboard</h1>
          </div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Admin Token</label>
          <input
            type="password"
            value={token}
            onChange={e => setToken(e.target.value)}
            placeholder="Enter ADMIN_SECRET"
            className="w-full px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 mb-3"
          />
          {error && <p className="text-red-400 text-sm mb-3">{error}</p>}
          <button
            type="submit"
            disabled={!token.trim() || loading}
            className="w-full py-2.5 rounded-lg bg-gradient-to-r from-violet-500 to-purple-600 text-white font-semibold text-sm disabled:opacity-50"
          >
            {loading ? 'Authenticating...' : 'Sign In'}
          </button>
        </form>
      </div>
    );
  }

  // Chart data for top pages
  const chartData = data?.topPages?.map(p => ({
    name: p.path.length > 20 ? p.path.slice(0, 20) + '…' : p.path,
    views: Number(p.views),
  })) || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Shield className="w-6 h-6 text-violet-400" />
          <h1 className="text-2xl font-bold text-white">Admin Dashboard</h1>
        </div>
        <div className="flex items-center gap-2">
          {RANGES.map(r => (
            <button
              key={r.value}
              onClick={() => setRange(r.value)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                range === r.value
                  ? 'bg-violet-500/20 text-violet-300 border border-violet-400/30'
                  : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'
              }`}
            >
              {r.label}
            </button>
          ))}
          <button
            onClick={() => fetchData(token, range)}
            disabled={loading}
            className="p-1.5 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 text-gray-400 disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {loading && !data ? (
        <div className="text-center py-12 text-gray-400">Loading analytics...</div>
      ) : error ? (
        <div className="text-center py-12 text-red-400">{error}</div>
      ) : data ? (
        <>
          {/* Overview Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={Eye} label="Page Views" value={Number(data.pageViews).toLocaleString()} color="violet" />
            <StatCard icon={Users} label="Sessions" value={Number(data.sessions).toLocaleString()} color="blue" />
            <StatCard icon={Mail} label="Subscribers" value={Number(data.subscribers).toLocaleString()} color="green" />
            <StatCard icon={TrendingUp} label="Active Ads" value={data.adStats?.length || 0} color="amber" />
          </div>

          {/* Top Pages Chart */}
          {chartData.length > 0 && (
            <div className="bg-white/5 border border-white/10 rounded-xl p-4 sm:p-5">
              <h2 className="text-lg font-bold text-white mb-4">Top Pages (last {range})</h2>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} layout="vertical" margin={{ left: 80, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis type="number" stroke="#6b7280" fontSize={12} />
                    <YAxis type="category" dataKey="name" stroke="#6b7280" fontSize={11} width={80} />
                    <Tooltip
                      contentStyle={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff', fontSize: '13px' }}
                    />
                    <Bar dataKey="views" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Top Pages Table */}
          <div className="bg-white/5 border border-white/10 rounded-xl p-4 sm:p-5">
            <h2 className="text-lg font-bold text-white mb-3">Top Pages</h2>
            <DataTable
              title="Top Pages"
              headers={['Path', 'Views', '% of Total']}
              rows={data.topPages?.map(p => [
                p.path,
                Number(p.views).toLocaleString(),
                data.pageViews > 0 ? ((Number(p.views) / Number(data.pageViews)) * 100).toFixed(1) + '%' : '—',
              ]) || []}
            />
          </div>

          {/* Top Referrers */}
          <div className="bg-white/5 border border-white/10 rounded-xl p-4 sm:p-5">
            <h2 className="text-lg font-bold text-white mb-3">Top Referrers</h2>
            <DataTable
              title="Referrers"
              headers={['Referrer', 'Sessions']}
              rows={data.topReferrers?.map(r => [r.referrer, Number(r.count).toLocaleString()]) || []}
            />
          </div>

          {/* Ad Campaigns */}
          <div className="bg-white/5 border border-white/10 rounded-xl p-4 sm:p-5">
            <h2 className="text-lg font-bold text-white mb-3">Ad Campaign Performance</h2>
            <DataTable
              title="Ad Campaigns"
              headers={['Advertiser', 'Slot', 'Dates', 'Impressions', 'Clicks', 'CTR']}
              rows={data.adStats?.map(a => [
                a.advertiser_name,
                a.slot_id,
                `${a.start_date} → ${a.end_date}`,
                Number(a.impressions).toLocaleString(),
                Number(a.clicks).toLocaleString(),
                Number(a.impressions) > 0
                  ? ((Number(a.clicks) / Number(a.impressions)) * 100).toFixed(2) + '%'
                  : '0%',
              ]) || []}
            />
          </div>

          {/* Recent Leads */}
          <div className="bg-white/5 border border-white/10 rounded-xl p-4 sm:p-5">
            <h2 className="text-lg font-bold text-white mb-3">Recent Consulting Leads</h2>
            <DataTable
              title="Leads"
              headers={['Name', 'Email', 'Org', 'Service', 'Status', 'Date']}
              rows={data.leads?.map(l => [
                l.full_name,
                l.email,
                l.organization || '—',
                l.service_interest || '—',
                l.status,
                new Date(l.created_at).toLocaleDateString(),
              ]) || []}
            />
          </div>
        </>
      ) : null}

      {/* Footer */}
      <div className="text-center py-4 text-xs text-gray-600">
        Powered by codeAmani Labs
      </div>
    </div>
  );
}
